// Renders parsed academy segments: plain `md` segments via the shared
// MarkdownText renderer, and the four special block kinds (summary/note/tip/qa)
// as V3-styled colored containers. Visual matches mockups/12-academy/proposal.html.

import type { ReactNode } from 'react';
import { MarkdownText } from '../../lib/markdown';
import type { AcademySegment } from '../../lib/academy-content';

type BlockKind = Exclude<AcademySegment['kind'], 'md'>;

interface BlockMeta {
  label: string;
  icon: string;
  containerClassName: string;
  labelClassName: string;
}

const BLOCK_META: Record<BlockKind, BlockMeta> = {
  summary: {
    label: 'Zusammenfassung',
    icon: '🔍',
    containerClassName: 'border-amber-500/30 bg-amber-500/[0.07]',
    labelClassName: 'text-amber-300',
  },
  note: {
    label: 'Wichtiger Hinweis',
    icon: '📌',
    containerClassName: 'border-rose-500/30 bg-rose-500/[0.07]',
    labelClassName: 'text-rose-300',
  },
  tip: {
    label: 'Tipp',
    icon: '💡',
    containerClassName: 'border-emerald-500/30 bg-emerald-500/[0.07]',
    labelClassName: 'text-emerald-300',
  },
  qa: {
    label: 'Verständnisfragen',
    icon: '❓',
    containerClassName: 'border-sky-500/30 bg-sky-500/[0.07]',
    labelClassName: 'text-sky-300',
  },
};

function Block({ kind, text }: { kind: BlockKind; text: string }): ReactNode {
  const meta = BLOCK_META[kind];
  return (
    <div className={`rounded-2xl border p-5 ${meta.containerClassName}`}>
      <div
        className={`mb-2 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider ${meta.labelClassName}`}
      >
        <span aria-hidden="true">{meta.icon}</span>
        {meta.label}
      </div>
      <div className="text-[15px] leading-relaxed text-slate-200">
        <MarkdownText text={text} />
      </div>
    </div>
  );
}

export function AcademySegments({
  segments,
}: {
  segments: readonly AcademySegment[];
}): ReactNode {
  return (
    <div className="flex flex-col gap-5">
      {segments.map((seg, i) =>
        seg.kind === 'md' ? (
          <div key={i} className="text-[15px] leading-relaxed text-slate-200">
            <MarkdownText text={seg.text} />
          </div>
        ) : (
          <Block key={i} kind={seg.kind} text={seg.text} />
        ),
      )}
    </div>
  );
}
